import { spawnMarketViewImportDialog } from "$root/functional-areas/market-view/Import";
import { spawnInvestmentImportDialogV2 } from "$root/functional-areas/portfolio/Import";
import { spawnInvestmentReferenceImportDialog } from "$root/functional-areas/portfolio/ImportReference";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { spawnUniverseImportDialog } from "$root/functional-areas/universe/Import";
import { getCurrentUser, useUserValue } from "$root/functional-areas/user";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { Button, DropdownMenu, type DropdownTriggerProps } from "@mdotm/shadcn/mdotm/components";
import { asyncStateFrom } from "@mdotm/utils/promise";
import CreateEntitiesButton from "./common/CreateEntities";
import { getApiGen } from "$root/api/factory";
import { useApiGen } from "$root/api/hooks";
import {
	type ReferenceImportConverterType,
	IntegrationsControllerV3ApiFactory,
	IntegrationUniverseControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import type { JSX } from "react";

function ImportTrigger({ state, ...props }: DropdownTriggerProps): JSX.Element {
	return (
		<Button
			state={asyncStateFrom(state)}
			progressBar={false}
			size="small"
			palette="primary"
			data-qualifier="PortfolioStudio/Import"
			{...props}
		>
			<Icon icon="Dowload" size={16} className="-rotate-90 mr-1" />
			Import
		</Button>
	);
}

export function PortfolioStudioTitleActions(): JSX.Element {
	const user = useUserValue();

	const integrationUniverseApi = useApiGen(IntegrationUniverseControllerApiFactory);

	const referenceConverters: ReferenceImportConverterType[] = [];
	if (user.importFormats?.includes("SPHERE_TEMPLATE_CONVERTER")) {
		referenceConverters.push("SPHERE_TARGET_PTF");
	}
	if (user.importFormats?.includes("SPHERE_BULK_TEMPLATE_CONVERTER")) {
		referenceConverters.push("SPHERE_BULK_TEMPLATE_CONVERTER");
	}

	return (
		<div className="flex items-center justify-between gap-2">
			{hasAccess(user, {
				requiredService: "IMPORT",
				requiredParam: ({ importFormats }) => importFormats !== undefined && importFormats.length > 0,
			}) ? (
				<DropdownMenu
					Trigger={ImportTrigger}
					actions={[
						{
							label: "Portfolio",
							onClickAsync: importPortfolio,
							"data-qualifier": "PortfolioStudio/Import/Portfolio",
						},
						...(hasAccess(user, {
							requiredService: "IMPORT",
							requiredParam: () => referenceConverters.length > 0,
						})
							? [
									{
										label: "Reference",
										onClick: () => {
											spawnInvestmentReferenceImportDialog({
												converters: referenceConverters,
												onImportFinished: ({ imported }) => {
													if (imported.length) {
														referenceRepository.invalidateLists();
													}
												},
											});
										},
										"data-qualifier": "PortfolioStudio/Import/Reference",
									},
								]
							: []),
						{
							label: "Universe",
							onClickAsync: async () => {
								const importConverters = await axiosExtract(
									integrationUniverseApi.retrieveUniverseImportConverterType(),
								);
								const converters = importConverters.filter((x) => user.importFormats?.includes(x));
								spawnUniverseImportDialog({
									converters,
									onImportFinished: ({ imported }) => {
										if (imported.length) {
											universeRepository.invalidateLists();
										}
									},
								});
							},
							"data-qualifier": "PortfolioStudio/Import/Universe",
						},
						...(hasAccess(user, {
							requiredService: "IMPORT",
							requiredParam: ({ importFormats }) => Boolean(importFormats?.includes("SPHERE_TEMPLATE_CONVERTER")),
						})
							? [
									{
										label: "Market View",
										onClick: () =>
											spawnMarketViewImportDialog({
												converters: ["SPHERE_TEMPLATE_CONVERTER"] as const,
												onImportFinished: ({ imported }) => {
													if (imported.length) {
														marketViewRepository.invalidateLists();
													}
												},
											}),
										"data-qualifier": "PortfolioStudio/Import/MarketView",
									},
								]
							: []),
					]}
				/>
			) : (
				<></>
			)}

			<CreateEntitiesButton />
		</div>
	);
}

export async function importPortfolio() {
	const integrationsApiV3 = getApiGen(IntegrationsControllerV3ApiFactory);
	const user = getCurrentUser();

	const importConverters = await axiosExtract(integrationsApiV3.retrieveInvestmentImportConverterType());
	const converters = importConverters.filter((converter) => user.importFormats?.includes(converter));

	spawnInvestmentImportDialogV2({
		converters,
		onImportFinished: ({ imported }) => {
			if (imported.length) {
				portfolioRepository.invalidateLists();
			}
		},
	});
}
